import { useEffect, useMemo, useState } from 'react';
import type { Nullable } from 'corgi-types';

import { useForm } from 'common/hooks/form';
import { useLocalization } from 'common/providers/LocaleProvider';
import { isFormValid } from 'common/utils/form/validation';
import { FieldName } from '../components/enums';
import type { UserData } from '../components/types';
import { getCustomerNumber, getInitialFormData, slicesIncludeField } from '../utils/form-data';
import { saveComment, saveImages } from '../utils/storage';
import { getValidationErrorKey, getValidationMap } from '../utils/validation';
import { useFormGroups } from './form-groups';
import type { UseSubmitForm } from './submit-form';
import { useSubmitForm } from './submit-form';

interface UseContactForm extends Pick<
  UseSubmitForm,
  'slices' | 'settings' | 'selectedChannel' | 'isChannelSelectionActive'
> {
  reason: string;
  isLoggedIn: boolean;
  userData: Nullable<UserData>;
  skuFromUrl: Nullable<string>;
  orderIdFromUrl: Nullable<string>;
  sellerName: Nullable<string>;
}

const useScrollIntoErrorAlert = (formSubmitError: boolean) => {
  useEffect(() => {
    if (formSubmitError) {
      const alert = document.querySelector('[data-testid="form-submit-alert"]');

      if (alert) {
        alert.scrollIntoView();
      }
    }
  }, [formSubmitError]);
};

export const useContactForm = ({
  slices,
  isLoggedIn,
  reason,
  userData,
  settings,
  selectedChannel,
  isChannelSelectionActive,
  skuFromUrl,
  orderIdFromUrl,
  sellerName,
}: UseContactForm) => {
  const { t } = useLocalization();
  const [formSubmitError, setFormSubmitError] = useState(false);
  const [callbackNotAvailableError, setCallbackNotAvailableError] = useState(false);
  const validationMap = useMemo(() => getValidationMap(slices), [slices]);
  const { formData, hasErrorMap, errorMessagesMap, handleInputChange } = useForm<FieldName>({
    validationMap,
    initialFormData: getInitialFormData({ user: userData, slices, skuFromUrl, orderIdFromUrl }),
    getValidationErrorKey,
  });

  const isFormInvalid = !isFormValid({ values: formData, validationMap });

  const { isLoading, isSuccess, submitForm } = useSubmitForm({
    slices,
    reason,
    settings,
    formData,
    isFormInvalid,
    selectedChannel,
    isChannelSelectionActive,
    setCallbackNotAvailableError,
    setError: setFormSubmitError,
    customerNumber: getCustomerNumber(userData),
    sellerName,
    orderId: orderIdFromUrl,
  });

  useScrollIntoErrorAlert(formSubmitError);

  useEffect(() => {
    if (slicesIncludeField(slices, FieldName.Comment)) {
      saveComment(formData.get(FieldName.Comment) ?? '');
    }
    if (slicesIncludeField(slices, FieldName.Images)) {
      saveImages(formData.get(FieldName.Images) ?? '');
    }
  }, [slices, formData]);

  const formGroups = useFormGroups({
    slices,
    formData,
    hasErrorMap,
    errorMessagesMap,
    handleInputChange,
    isLoggedIn,
    reason,
    translate: t,
    isSellerContactMode: !!sellerName,
  });

  return {
    isLoading,
    isSuccess,
    formGroups,
    submitForm,
    isFormInvalid,
    formSubmitError,
    callbackNotAvailableError,
    setCallbackNotAvailableError,
  };
};
